import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PanelState, { extractApiError } from "@/components/PanelState";
import { Scale, AlertTriangle, RefreshCw, ChevronRight, ChevronDown, Search as SearchIcon, Settings } from "lucide-react";

// ============================================================================
// Collections (P1, 2026-08-11) — DISQUALIFIED list only. No eligible view,
// no "N eligible" counter, no L10 inclusion. Explicit scope ceiling from
// VPS: the module has to prove disqualifications at scale before an
// eligible list can be trusted. Any UI hinting at an eligible list ships
// broken trust, not helpful preview.
// ============================================================================

const REASON_LABEL = {
  PAYMENT_MISAPPLIED: "Payment misapplied",
  OFFSETTING_CREDIT: "Offsetting credit",
  DISPUTED_INVOICE: "Disputed invoice",
  WRITE_OFF_PENDING: "Write-off pending",
  ACCOUNT_ON_HOLD: "Account on hold",
  BALANCE_UNDER_MINIMUM: "Balance under minimum",
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function ReasonPill({ code, amount }) {
  const label = REASON_LABEL[code] || code;
  return (
    <span data-testid={`collections-reason-${code}`} className="inline-flex items-center gap-1.5 text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 bg-[#fef3c7] text-[#854d0e] border-[#facc15]">
      {label}
      {amount != null && <span className="opacity-70">· {fmt$(amount)}</span>}
    </span>
  );
}

export default function Collections() {
  const { user } = useAuth();
  const canEditSettings = !!user && ["platform_admin", "owner", "administrative"].includes(user.role);
  const [bucket, setBucket] = useState("katt");
  const [reasonFilter, setReasonFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [expanded, setExpanded] = useState(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoadState({ status: "loading", error: null });
    try {
      const params = { bucket };
      if (reasonFilter) params.reason_code = reasonFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data: resp } = await api.get("/collections/disqualified", { params });
      setData(resp);
      if (!silent) setLoadState({ status: "ok", error: null });
    } catch (err) {
      if (!silent) { setData(null); setLoadState({ status: "error", error: extractApiError(err) }); }
    }
  };

  useEffect(() => { load(); }, [bucket, reasonFilter, debouncedSearch]);

  const snap = data?.snapshot;
  const settings = data?.settings || { bucket_threshold: 1000 };
  const counts = data?.counts || { katt: 0, gold: 0 };
  const debtors = data?.debtors || [];
  const gated = data?.gated === true;

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totalDollars = debtors.reduce((s, d) => s + (d.debtor_total || 0), 0);

  return (
    <div className="space-y-5" data-testid="collections-page">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[.15em] text-[#0072bc] font-bold">
            <Scale size={14} strokeWidth={2} />
            <span>Collections · Disqualified list</span>
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">
            Collections review
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1 max-w-[640px]">
            Accounts VPS flagged as disqualified from collections and why. Sub-{fmt$(settings.bucket_threshold)} routes to Katt; {fmt$(settings.bucket_threshold)}+ routes to Gold.
            Eligible list is intentionally out of scope for P1.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load()} data-testid="collections-refresh" className="p-2 rounded-lg border border-[#e7e9ee] bg-white hover:bg-[#f5f6f8]" title="Refresh">
            <RefreshCw size={14} strokeWidth={1.8} />
          </button>
          {canEditSettings && (
            <button onClick={() => setSettingsOpen(true)} data-testid="collections-settings-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e7e9ee] bg-white hover:bg-[#f5f6f8] text-[12px] font-semibold">
              <Settings size={13} strokeWidth={1.8} />
              Threshold
            </button>
          )}
        </div>
      </header>

      {/* Snapshot header — reconciliation state is the most important
          signal on the page. Red banner blocks the list when unreconciled. */}
      {snap && (
        <div className={`rounded-xl p-4 border ${snap.unreconciled ? "bg-[#fee2e2] border-[#f87171]" : "bg-white border-[#e7e9ee]"}`} data-testid="collections-snapshot-header">
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">As of</div>
              <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-1">{fmtDate(snap.as_of_date)}</div>
              <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5">
                sync <span data-testid="collections-sync-run-id">{snap.sync_run_id}</span>
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Debtors on this snapshot</div>
              <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-1">{snap.debtor_count}</div>
              <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5">
                {snap.disqualified_count} disqualified · {snap.pending_count} pending
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Aging screen total</div>
              <div className="font-mono-data font-bold text-[22px] text-[#0072bc] mt-1">{fmt$(snap.reconciliation.aging_screen_total)}</div>
              <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5">
                computed {fmt$(snap.reconciliation.computed_total)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Reconciliation</div>
              <div data-testid="collections-reconciliation-state" className={`font-mono-data font-bold text-[16px] mt-1 ${snap.unreconciled ? "text-[#991b1b]" : "text-[#166534]"}`}>
                {snap.unreconciled ? "UNRECONCILED" : "MATCHED"}
              </div>
            </div>
          </div>
          {snap.unreconciled && (
            <div className="mt-3 pt-3 border-t border-[#f87171] flex items-start gap-3">
              <AlertTriangle size={18} strokeWidth={1.8} className="text-[#991b1b] mt-0.5 shrink-0" />
              <div className="text-[13px] text-[#7a1c1c]">
                <b>Disqualified list is hidden.</b> This snapshot&apos;s totals don&apos;t reconcile with the live aging screen —
                acting on it would risk approving or dismissing accounts against stale data.
                The list will re-appear on the next reconciled ingest from VPS. Contact VPS if this persists across multiple runs.
              </div>
            </div>
          )}
        </div>
      )}

      {!snap && loadState.status === "ok" && (
        <PanelState status="ok" isEmpty emptyTitle="No snapshot yet" emptyBody={<>Waiting for VPS&apos;s first collections ingest to arrive.</>} testIdPrefix="collections-empty" />
      )}

      {snap && !gated && (
        <>
          {/* Bucket tabs */}
          <div className="flex flex-wrap gap-2" data-testid="collections-bucket-tabs">
            {[
              { key: "katt", label: `Sub-${fmt$(settings.bucket_threshold)} (Katt)`, count: counts.katt },
              { key: "gold", label: `${fmt$(settings.bucket_threshold)}+ (Gold)`, count: counts.gold },
            ].map((t) => {
              const active = bucket === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setBucket(t.key)}
                  data-testid={`collections-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-lg border transition ${
                    active ? "bg-[#0e1420] text-white border-[#0e1420]" : "bg-white text-[#0e1420] border-[#e7e9ee] hover:bg-[#f5f6f8]"
                  }`}
                >
                  <span>{t.label}</span>
                  <span className={`min-w-[22px] text-center px-1.5 py-0.5 rounded-md text-[10.5px] font-mono-data ${active ? "bg-white/15 text-white" : "bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]"}`}>
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <SearchIcon size={13} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--cc-ink-muted)]" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="collections-search"
                placeholder="Search customer name or ID…" className="pl-8 pr-3 py-2 w-72 text-[13px] border border-[#e7e9ee] rounded-lg bg-white outline-none focus:border-[#0072bc]" />
            </div>
            <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} data-testid="collections-reason-filter"
              className="px-3 py-2 text-[13px] border border-[#e7e9ee] rounded-lg bg-white outline-none">
              <option value="">All reason codes</option>
              {Object.entries(REASON_LABEL).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            <div className="flex-1" />
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] font-mono-data" data-testid="collections-total-line">
              {debtors.length} debtors · {fmt$(totalDollars)}
            </div>
          </div>

          {/* Table */}
          {debtors.length === 0 ? (
            <PanelState status="ok" isEmpty emptyTitle="No disqualified accounts match your filter" emptyBody={<>Try clearing the reason-code filter or switching buckets.</>} testIdPrefix="collections-list-empty" />
          ) : (
            <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] border-b border-[#eef1f6]">
                  <tr>
                    <th className="w-[36px] px-3 py-2.5"></th>
                    <th className="text-left px-3 py-2.5">Customer</th>
                    <th className="text-right px-3 py-2.5">Debtor total</th>
                    <th className="text-left px-3 py-2.5">Reason(s)</th>
                    <th className="text-left px-3 py-2.5">D7</th>
                  </tr>
                </thead>
                <tbody>
                  {debtors.map((d) => {
                    const isOpen = expanded.has(d.customer_id);
                    return (
                      <Fragment key={d.customer_id}>
                        <tr data-testid={`collections-row-${d.customer_id}`} className="border-b border-[#eef1f6] last:border-b-0 hover:bg-[#f9fafb]">
                          <td className="px-3 py-3">
                            <button onClick={() => toggle(d.customer_id)} data-testid={`collections-expand-${d.customer_id}`} className="p-1 hover:bg-[#f5f6f8] rounded">
                              {isOpen ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                            </button>
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-[13px] font-semibold text-[#0e1420]">{d.customer_name}</div>
                            <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">#{d.customer_id}</div>
                          </td>
                          <td className="px-3 py-3 text-right font-mono-data font-bold text-[#0072bc]">
                            {fmt$(d.debtor_total)}
                            {Math.abs(d.customer_balance - d.sum_open_invoices) >= 0.01 && (
                              <div className="text-[10px] font-normal text-[color:var(--cc-ink-muted)] mt-0.5" title={`customer_balance=${fmt$(d.customer_balance)} · sum_open_invoices=${fmt$(d.sum_open_invoices)}`}>
                                bal/invoice mismatch
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {d.disqualifications.map((dq, i) => (
                                <ReasonPill key={i} code={dq.reason_code} amount={dq.dollar_amount} />
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            {d.already_referred ? (
                              <div data-testid={`collections-d7-${d.customer_id}`} className="inline-flex flex-col">
                                <span className="text-[10.5px] font-mono-data uppercase tracking-widest bg-[#e5e7eb] text-[#4b5563] border border-[#9ca3af] rounded px-2 py-0.5">
                                  referred to counsel
                                </span>
                                <span className="text-[10px] text-[color:var(--cc-ink-muted)] mt-0.5">{d.referred_to} · {fmtDate(d.referred_to_counsel_at)}</span>
                              </div>
                            ) : (
                              <span className="text-[11px] text-[color:var(--cc-ink-muted)]">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-[#f9fafb] border-b border-[#eef1f6]">
                            <td colSpan={5} className="px-6 py-4">
                              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-2">
                                Raw evidence per disqualification
                              </div>
                              <div className="space-y-3">
                                {d.disqualifications.map((dq, i) => (
                                  <div key={i} className="bg-white border border-[#e7e9ee] rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <ReasonPill code={dq.reason_code} amount={dq.dollar_amount} />
                                    </div>
                                    <pre data-testid={`collections-evidence-${d.customer_id}-${i}`} className="text-[11.5px] font-mono-data bg-[#0e1420] text-[#e7e9ee] rounded p-3 overflow-x-auto">
{JSON.stringify(dq.raw_evidence, null, 2)}
                                    </pre>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {settingsOpen && (
        <ThresholdModal
          current={settings.bucket_threshold}
          onClose={() => setSettingsOpen(false)}
          onSaved={async () => { setSettingsOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

function ThresholdModal({ current, onClose, onSaved }) {
  const [val, setVal] = useState(String(current));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    const num = Number(val);
    if (Number.isNaN(num) || num <= 0) { setErr("Threshold must be a positive number"); setSaving(false); return; }
    try {
      await api.patch("/collections/settings", { bucket_threshold: num });
      await onSaved();
    } catch (e) {
      setErr(extractApiError(e)?.message || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4" onClick={onClose} data-testid="collections-threshold-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-[440px] shadow-xl border border-[#e7e9ee] my-8">
        <div className="px-5 py-4 border-b border-[#e7e9ee]">
          <h2 className="font-display font-bold text-[17px] text-[#0e1420]">Bucket threshold</h2>
          <p className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5">Debtors with total <b>&lt;</b> this value route to Katt; <b>{"\u2265"}</b> routes to Gold. Applies on the next reconciled ingest — existing buckets don&apos;t rebucket mid-cycle.</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Threshold ($)</label>
          <input type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} data-testid="collections-threshold-input"
            className="w-full border border-[#e7e9ee] rounded-md px-3 py-2 text-[13px] font-mono-data bg-white outline-none focus:border-[#0072bc]" required />
          {err && <div className="text-[12px] text-[#991b1b]">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-[#e7e9ee] bg-[#f9fafb] rounded-b-xl flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-white">Cancel</button>
          <button type="submit" disabled={saving} data-testid="collections-threshold-save" className="px-4 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a95] disabled:opacity-60">
            {saving ? "Saving…" : "Save threshold"}
          </button>
        </div>
      </form>
    </div>
  );
}
